import { observer } from 'mobx-react-lite';
import { Icon } from '@fluentui/react/lib/Icon';
import type { ITheme } from '@fluentui/react/lib/Styling';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { onIsOnlineMeetingChanged } from 'owa-calendar-compose-form-actions';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import generateDefaultDropdownStyles from 'owa-component-styling-utils/lib/utils/colorSchemesToStyles/generateDefaultDropdownStyles';
import generateDropdownCalloutStyles from 'owa-component-styling-utils/lib/utils/generateDropdownCalloutStyles';
import loc from 'owa-localize';
import * as React from 'react';
import {
    addOnlineMeeting,
    removeOnlineMeeting,
    onlineMeetingsTooltipText,
} from './OnlineMeetingDropdownToggle.locstring.json';
import {
    isSupportedOnlineMeetingProvider,
    getDefaultOnlineMeetingProvider,
    getIconFromProviderName,
    getOnlineProviderLabel,
    OnlineMeetingProviderType,
} from 'owa-online-meeting';
import { Dropdown, IDropdownOption, DropdownMenuItemType } from '@fluentui/react/lib/Dropdown';
import isOnlineMeeting from 'owa-calendar-compose-form-store/lib/selectors/isOnlineMeeting';
import { LazyOnlineMeetingDefaultOnDialog } from 'owa-calendar-compose-form-utils';

import styles from './OnlineMeetingDropdown.scss';

/**
 * the dropdown option key which represents that no online meeting provider is selected.
 * Note that the value has to be null for 'None' so that clicking on it will reset the option to the placeholder
 */
const NONE_ONLINE_MEETING_PROVIDER_KEY = 'none_1';

export interface OnlineMeetingDropdownProps {
    providers: OnlineMeetingProviderType[];
    theme: ITheme;
    event: CalendarEvent;
    colorScheme: ColorScheme;
    formId: string;
    dropdownContainerClassName?: string;
    hideDropdownTitleIcon?: boolean;
    disabled?: boolean;
    shouldShowTooltip?: boolean;
}

export default observer(function OnlineMeetingDropdown(props: OnlineMeetingDropdownProps) {
    const getOnlineMeetingProviderOptions = (): IDropdownOption[] => {
        // Put the default provider at the front of the provider list
        const defaultProvider = getDefaultOnlineMeetingProvider(props.event);
        const reorderedProviders = [...props.providers].filter(p => p !== defaultProvider);
        reorderedProviders.unshift(defaultProvider);
        // Add all the allowed provider options
        const providerOptions: IDropdownOption[] = reorderedProviders.map(provider => {
            return {
                key: provider as string,
                text: getOnlineProviderLabel(provider),
                itemType: DropdownMenuItemType.Normal,
                isSelected: props.event.OnlineMeetingProvider == provider,
                data: {
                    icon: getIconFromProviderName(provider),
                },
            };
        });
        if (props.event.OnlineMeetingProvider) {
            // Add the divider and 'None' if there is currently a valid provider selected in the dropdown
            providerOptions.push(
                {
                    key: 'divider_1',
                    text: '-',
                    itemType: DropdownMenuItemType.Divider,
                },
                {
                    key: NONE_ONLINE_MEETING_PROVIDER_KEY,
                    text: loc(removeOnlineMeeting),
                    itemType: DropdownMenuItemType.Normal,
                }
            );
        }
        return providerOptions;
    };
    const onRenderOptionHandler = (option: IDropdownOption, hideIcon?: boolean): JSX.Element => {
        return (
            <div className={styles.option}>
                {option.data?.icon && !hideIcon && (
                    <div className={styles.iconContainer}>
                        <Icon
                            styles={{
                                root: {
                                    color: props.colorScheme.accessibleText.msColorWhiteBackground,
                                },
                            }}
                            iconName={option.data.icon}
                            aria-hidden="true"
                            title={option.data.icon}
                        />
                    </div>
                )}
                <span>{option.text}</span>
            </div>
        );
    };

    const onProviderChanged = React.useCallback(
        (ev: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => {
            const selectedProviderKey = option.key as OnlineMeetingProviderType;
            const isOnlineMeeting = isSupportedOnlineMeetingProvider(selectedProviderKey);
            const onlineMeetingProvider = isOnlineMeeting ? selectedProviderKey : null;
            onIsOnlineMeetingChanged(
                isOnlineMeeting,
                onlineMeetingProvider,
                true /* isOnlineMeetingExplicitlySet */,
                false /* wasOnlineMeetingEverAutoSet */,
                props.formId
            );
        },
        [props.formId]
    );

    const onRenderOption = (option: IDropdownOption): JSX.Element =>
        onRenderOptionHandler(option, false /* hideIcon */);
    const onRenderTitle = (options: IDropdownOption[]): JSX.Element => {
        const option = options[0];
        if (option.text == null) {
            option.text = loc(addOnlineMeeting);
        }
        return onRenderOptionHandler(option, props.hideDropdownTitleIcon);
    };
    const defaultDropdownStyles = generateDefaultDropdownStyles(props.colorScheme, {
        title: { paddingLeft: '0px' },
    });

    const getSelectedOption = (options: IDropdownOption[]): IDropdownOption => {
        const selectedOptions = options?.filter(option => option?.isSelected);
        return selectedOptions && selectedOptions.length > 0 ? selectedOptions[0] : null;
    };

    const options = getOnlineMeetingProviderOptions();
    const selectedOption = getSelectedOption(options);
    const tooltipText =
        props.shouldShowTooltip && !selectedOption ? loc(onlineMeetingsTooltipText) : '';

    const toggleRef = React.useRef<HTMLDivElement>();
    const onTarget = () => {
        return toggleRef.current;
    };
    const [showCallOut, setShowCallOut] = React.useState(false);
    React.useEffect(() => {
        if (toggleRef.current) {
            setShowCallOut(true);
        }
    }, [toggleRef.current]);

    return (
        <>
            <div className={props.dropdownContainerClassName}>
                <TooltipHost content={tooltipText}>
                    <Dropdown
                        ref={toggleRef}
                        calloutProps={{ styles: generateDropdownCalloutStyles() }}
                        onChange={onProviderChanged}
                        onRenderOption={onRenderOption}
                        onRenderTitle={onRenderTitle}
                        options={options}
                        selectedKey={selectedOption ? selectedOption.key : -1}
                        placeHolder={loc(addOnlineMeeting)}
                        styles={defaultDropdownStyles}
                        theme={props.theme}
                        disabled={props.disabled}
                    />
                </TooltipHost>
            </div>
            {isOnlineMeeting(props.formId) && showCallOut && (
                <LazyOnlineMeetingDefaultOnDialog target={onTarget} />
            )}
        </>
    );
});
